import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable, Subject, Subscription } from 'rxjs';
import { ImportItem } from './import.component';
import { FaceDetection } from '../../shared/face-matching/face-matching.component';
import { bufferTime, skipWhile } from 'rxjs/operators';
import { AutocompleteOption } from '../../shared/field-text/field-text.component';
import {
  ErrorResponse,
  IdsList,
  MediaItem,
  ModelError,
  Translatable,
  UploadSession,
  UploadSessionPublishRequest,
  UploadSessionsService,
} from 'piwe-front-swagger-client';
import {
  Agency,
  Author,
  Category,
  Keyword,
  LicenceType,
  Person,
  SpecialOfferType,
  Tag,
} from '../../../../projects/piwe-front-swagger-client/src';
import { LoadingService } from '../../shared/loading.service';
import { SnackBarService } from '../../share/snack-bar.service';
import { ComputeFields } from '../../shared/compute-fields';
import logging from '../../logging';
import { AIStatusTrackingService } from '../content/products/ai-status-tracking.service';

@Injectable({
  providedIn: 'root',
})
export class ImportService {

  constructor(
    public uploadService: UploadSessionsService,
    public loadingService: LoadingService,
    public snackBarService: SnackBarService,
    private aiStatusTrackingService: AIStatusTrackingService
  ) {
    this.items = [];
    this.itemsById = {};

    this.setUpSaveSession();

    // Pretplata na ažuriranja statusa AI-a
    this.aiStatusTrackingService.statusUpdates
      // .pipe(takeUntil(this.unsubscribe$))
      .subscribe(updates => {

        let itemsUpdated = false;

        updates.forEach(update => {
          const item = this.items.find(i => i.id === update.productId);
          if (item) {
            if (update.aiStatus && update.aiStatus !== item.aiStatus) {
              item.aiStatus = update.aiStatus;
              itemsUpdated = true;
            }

            if (item.isAiProcessing !== update.isProcessing) {
              item.isAiProcessing = update.isProcessing;
              itemsUpdated = true;
            }

            if (update.isProcessing && update.aiStatus && itemsUpdated) {
                this.snackBarService.showSnackBar(update.aiStatus, 'INFO');
            }
          }
        });

        // Ako je bilo izmjena, ažuriraj UI
        if (itemsUpdated) {
          this.itemsSource.next(this.items);
        }
      });
  }
  private files: any[] = [];

  public aiCropSource = new BehaviorSubject<boolean | undefined>(undefined);
  public aiCropData: Observable<boolean | undefined> =
    this.aiCropSource.asObservable();

  public aiContentSource = new BehaviorSubject<boolean | undefined>(undefined);
  public aiContentData: Observable<boolean | undefined> =
    this.aiContentSource.asObservable();

  public aiFaceSource = new BehaviorSubject<boolean | undefined>(undefined);
  public aiFaceData: Observable<boolean | undefined> =
    this.aiFaceSource.asObservable();

  private selectItemsSource = new Subject<number[]>();
  public selectItemsData: Observable<number[]> =
    this.selectItemsSource.asObservable();

  private refreshEditingErrorsSource = new Subject<void>();
  public refreshEditingErrorsData: Observable<void> =
    this.refreshEditingErrorsSource.asObservable();

  private selectAllSource = new Subject<boolean>();
  public selectAllData: Observable<boolean> =
    this.selectAllSource.asObservable();

  private uploadingFilesSource = new Subject<any[]>();
  public uploadingFilesData: Observable<any[]> =
    this.uploadingFilesSource.asObservable();

  private itemsSource = new Subject<ImportItem[]>();
  public itemsData: Observable<ImportItem[]> = this.itemsSource.asObservable();

  private selectedIdsSource = new Subject<number[]>();
  public selectedIdsData: Observable<number[]> =
    this.selectedIdsSource.asObservable();

  public items: ImportItem[] = [];
  private itemsById: { [key: number]: ImportItem } = {};

  private selectedIds: number[] = [];

  private editingHeadlineSource = new Subject<AutocompleteOption | undefined>();
  public editingHeadlineData: Observable<AutocompleteOption | undefined> =
    this.editingHeadlineSource.asObservable();

  private editingCaptionSource = new Subject<string | undefined>();
  public editingCaptionData: Observable<string | undefined> =
    this.editingCaptionSource.asObservable();

  private editingPeopleSource = new Subject<AutocompleteOption[] | undefined>();
  public editingPeopleData: Observable<AutocompleteOption[] | undefined> =
    this.editingPeopleSource.asObservable();

  private editingKeywordsSource = new Subject<
    AutocompleteOption[] | undefined
  >();
  public editingKeywordsData: Observable<AutocompleteOption[] | undefined> =
    this.editingKeywordsSource.asObservable();

  private videoTitleSource = new Subject<string | undefined>();
  public videoTitleData: Observable<string | undefined> =
    this.videoTitleSource.asObservable();

  private editingCategoriesSource = new Subject<
    AutocompleteOption | undefined
  >();
  public editingCategoriesData: Observable<AutocompleteOption | undefined> =
    this.editingCategoriesSource.asObservable();

  private editingAuthorSource = new Subject<AutocompleteOption | undefined>();
  public editingAuthorData: Observable<AutocompleteOption | undefined> =
    this.editingAuthorSource.asObservable();

  private editingAgencySource = new Subject<AutocompleteOption | undefined>();
  public editingAgencyData: Observable<AutocompleteOption | undefined> =
    this.editingAgencySource.asObservable();

  private editingTagSource = new Subject<AutocompleteOption | undefined>();
  public editingTagData: Observable<AutocompleteOption | undefined> =
    this.editingTagSource.asObservable();

  private editingSpecialOfferSource = new Subject<boolean>();
  public editingSpecialOfferData: Observable<boolean> =
    this.editingSpecialOfferSource.asObservable();

  private editingSpecialOfferTypesSource = new Subject<
    AutocompleteOption[] | undefined
  >();
  public editingSpecialOfferTypesData: Observable<
    AutocompleteOption[] | undefined
  > = this.editingSpecialOfferTypesSource.asObservable();

  private editingApiFtpSource = new Subject<boolean>();
  public editingApiFtpData: Observable<boolean> =
    this.editingApiFtpSource.asObservable();

  private editingLicenceSource = new Subject<string | undefined>();
  public editingLicenceData: Observable<string | undefined> =
    this.editingLicenceSource.asObservable();

  private editingDateCreatedSource = new Subject<string | undefined>();
  public editingDateCreatedData: Observable<string | undefined> =
    this.editingDateCreatedSource.asObservable();

  private editingNeedsTranslationEnSource = new Subject<boolean>();
  public editingNeedsTranslationEnData: Observable<boolean> =
    this.editingNeedsTranslationEnSource.asObservable();

  private editingCaptionEnSource = new Subject<string | undefined>();
  public editingCaptionEnData: Observable<string | undefined> =
    this.editingCaptionEnSource.asObservable();

  private editingKeywordsEnSource = new Subject<
    AutocompleteOption[] | undefined
  >();
  public editingKeywordsEnData: Observable<AutocompleteOption[] | undefined> =
    this.editingKeywordsEnSource.asObservable();

  private editingWebPriceSource = new Subject<number | undefined>();
  public editingWebPriceData: Observable<number | undefined> =
    this.editingWebPriceSource.asObservable();

  private editingPrintPriceSource = new Subject<number | undefined>();
  public editingPrintPriceData: Observable<number | undefined> =
    this.editingPrintPriceSource.asObservable();

  private editingWebPriceEurSource = new Subject<number | undefined>();
  public editingWebPriceEurData: Observable<number | undefined> =
    this.editingWebPriceEurSource.asObservable();

  private editingPrintPriceEurSource = new Subject<number | undefined>();
  public editingPrintPriceEurData: Observable<number | undefined> =
    this.editingPrintPriceEurSource.asObservable();

  private editingFacesSource = new Subject<FaceDetection[]>();
  public editingFacesData: Observable<FaceDetection[]> =
    this.editingFacesSource.asObservable();

  private editingSuffixSource = new Subject<string | undefined>();
  public editingSuffixData: Observable<string | undefined> =
    this.editingSuffixSource.asObservable();

  private currentErrorResponse?: ErrorResponse;
  private errorResponseSource = new Subject<ErrorResponse | undefined>();
  public errorResponseData: Observable<ErrorResponse | undefined> =
    this.errorResponseSource.asObservable();

  private errorIdsSource = new Subject<number[]>();
  public errorIdsData: Observable<number[]> =
    this.errorIdsSource.asObservable();

  private saveQueueSource = new Subject<void>();
  public saveQueueData: Observable<void> = this.saveQueueSource.asObservable();

  private savingStatusSource = new BehaviorSubject<boolean>(false);
  public saveStatusData: Observable<boolean> =
    this.savingStatusSource.asObservable();

  public lastModifiedAt?: Date = undefined;
  public blockingOperations: {
    subscription: Subscription;
    tag: string;
    type: string;
  }[] = [];

  public isSaving = false;
  public lastLoadedTime?: Date = undefined;

  public isAiStatusDirty = false;

  // Polja koja su trenutno u procesu uređivanja - ne bi ih trebalo prebrisati kod server refresh-a
  private fieldsBeingEdited: Set<string> = new Set();

  uuidv4() {
    // @ts-ignore
    return ([1e7] + -1e3 + -4e3 + -8e3 + -1e11).replace(/[018]/g, (c) =>
      (
        c ^
        (crypto.getRandomValues(new Uint8Array(1))[0] & (15 >> (c / 4)))
      ).toString(16)
    );
  }

  // Dodaj ID polja u set polja koja se uređuju
  public markFieldAsBeingEdited(fieldName: string): void {
    this.fieldsBeingEdited.add(fieldName);
  }

  // Ukloni ID polja iz seta polja koja se uređuju
  public unmarkFieldAsBeingEdited(fieldName: string): void {
    this.fieldsBeingEdited.delete(fieldName);
  }

  loadSession() {
    if (this.blockingOperations.length) {
      logging.info(
        'import.service.ts',
        `Already loading, won't start again.`,
        this.blockingOperations
      );
      return;
    }

    const tag: string = this.uuidv4();

    const subscription: Subscription = this.uploadService
      .cmsGetUploadSession(undefined) // TODO: I have turned off diffing.
      .pipe(skipWhile((x) => this.isSaving))
      .subscribe(
        (uploadSession) => {
          // Start tracking status for this upload session
          if (uploadSession && uploadSession.id) {
            this.aiStatusTrackingService.startTracking(uploadSession.id);
          }

          if (uploadSession.last_modified_at) {
            this.lastModifiedAt = new Date(
              uploadSession.last_modified_at + 'Z'
            );
          }

          if (!this.isAiStatusDirty) {
            this.aiContentSource.next(uploadSession.ai_content);
            this.aiFaceSource.next(uploadSession.ai_face);
            this.aiCropSource.next(uploadSession.ai_crop);
          }

          const newItems = this.deserializeUploadSession(uploadSession);
          const difference = newItems.filter(
            (x) => !this.items.map((i) => i.id).includes(x.id)
          );
          this.items = this.items.concat(difference);

          newItems.forEach((newItem) => {
            if (this.itemsById[newItem.id] === undefined) {
              // Potpuno nov item - dodaj ga
              this.itemsById[newItem.id] = newItem;
            } else {
              // Item već postoji - pažljivo ažuriraj samo ako su različiti
              const existingItem = this.itemsById[newItem.id];

              if (newItem.dateImported !== undefined && newItem.dateImported !== existingItem.dateImported) {
                existingItem.dateImported = newItem.dateImported;
              }

              // Polja koja se ažuriraju samo ako su različita
              if (newItem.title !== undefined && newItem.title !== existingItem.title) {
                existingItem.title = newItem.title;
              }

              // Ne ažuriramo polja koja su trenutno u procesu uređivanja
              // Kompleksni objekti - poređenje po ključnim svojstvima
              if (!this.fieldsBeingEdited.has('headline') &&
                newItem.headline !== undefined &&
                (existingItem.headline === undefined ||
                  newItem.headline.title !== existingItem.headline.title ||
                  newItem.headline.value !== existingItem.headline.value)) {
                existingItem.headline = newItem.headline;
              }

              if (!this.fieldsBeingEdited.has('headlineEn') &&
                newItem.headlineEn !== undefined &&
                (existingItem.headlineEn === undefined ||
                  newItem.headlineEn.title !== existingItem.headlineEn.title ||
                  newItem.headlineEn.value !== existingItem.headlineEn.value)) {
                existingItem.headlineEn = newItem.headlineEn;
              }

              if (!this.fieldsBeingEdited.has('caption') &&
                newItem.caption !== undefined && newItem.caption !== existingItem.caption) {
                existingItem.caption = newItem.caption;
              }

              if (!this.fieldsBeingEdited.has('captionEn') &&
                newItem.captionEn !== undefined && newItem.captionEn !== existingItem.captionEn) {
                existingItem.captionEn = newItem.captionEn;
              }

              if (newItem.isAiProcessing !== undefined && newItem.isAiProcessing !== existingItem.isAiProcessing) {
                existingItem.isAiProcessing = newItem.isAiProcessing;
              }

              if (newItem.aiStatus !== undefined && newItem.aiStatus !== existingItem.aiStatus) {
                existingItem.aiStatus = newItem.aiStatus;
              }

              // Update face detections if they've changed
              if (newItem.faces && newItem.faces.length > 0) {
                if (!existingItem.faces) {
                  existingItem.faces = newItem.faces;
                } else {
                  let facesChanged = false;

                  // Update faces individually
                  newItem.faces.forEach(newFace => {
                    // Skip faces being edited by the user
                    if (this.fieldsBeingEdited.has(`face-person-${newFace.id}`)) {
                      return;
                    }

                    const existingFaceIndex = existingItem.faces!.findIndex(f => f.id === newFace.id);
                    if (existingFaceIndex >= 0) {
                      // Update existing face properties if different
                      const existingFace = existingItem.faces![existingFaceIndex];

                      // Only update if there are actual differences
                      if (existingFace.person?.value !== newFace.person?.value ||
                          existingFace.x !== newFace.x ||
                          existingFace.y !== newFace.y ||
                          existingFace.width !== newFace.width ||
                          existingFace.height !== newFace.height) {

                        // Preserve existing thumbnail and person if being edited
                        const updatedFace = {
                          ...newFace,
                          thumbnail: existingFace.thumbnail || newFace.thumbnail,
                          person: existingFace.person || newFace.person
                        };

                        existingItem.faces![existingFaceIndex] = updatedFace;
                        facesChanged = true;
                      }
                    } else {
                      // Add new face
                      existingItem.faces!.push(newFace);
                      facesChanged = true;
                    }
                  });

                  // Check for faces that were removed on the server
                  const newFaceIds = new Set(newItem.faces.map(f => f.id));
                  const facesToRemove = existingItem.faces.filter(f =>
                    !newFaceIds.has(f.id) && !this.fieldsBeingEdited.has(`face-person-${f.id}`)
                  );

                  if (facesToRemove.length > 0) {
                    existingItem.faces = existingItem.faces.filter(f =>
                      newFaceIds.has(f.id) || this.fieldsBeingEdited.has(`face-person-${f.id}`)
                    );
                    facesChanged = true;
                  }

                  // Re-emit the faces for the UI if this item is selected and faces changed
                  if (facesChanged && this.selectedIds.includes(newItem.id)) {
                    this.loadDataForSelectedItem();
                  }
                }
              }

              // Nizovi - usporedba po dužini i sadržaju
              if (!this.fieldsBeingEdited.has('people') &&
                newItem.people !== undefined &&
                (existingItem.people === undefined ||
                  !areArraysEqual(newItem.people, existingItem.people, (a: any, b: any) => a.value === b.value && a.title === b.title))) {
                existingItem.people = newItem.people;
              }

              if (!this.fieldsBeingEdited.has('keywords') &&
                newItem.keywords !== undefined &&
                (existingItem.keywords === undefined ||
                  !areArraysEqual(newItem.keywords, existingItem.keywords, (a: any, b: any) => a.value === b.value && a.title === b.title))) {
                existingItem.keywords = newItem.keywords;
              }

              if (!this.fieldsBeingEdited.has('keywordsEn') &&
                newItem.keywordsEn !== undefined &&
                (existingItem.keywordsEn === undefined ||
                  !areArraysEqual(newItem.keywordsEn, existingItem.keywordsEn, (a: any, b: any) => a.value === b.value && a.title === b.title))) {
                existingItem.keywordsEn = newItem.keywordsEn;
              }

              if (!this.fieldsBeingEdited.has('categories') &&
                newItem.categories !== undefined &&
                (existingItem.categories === undefined ||
                  newItem.categories.title !== existingItem.categories.title ||
                  newItem.categories.value !== existingItem.categories.value)) {
                existingItem.categories = newItem.categories;
              }

              if (!this.fieldsBeingEdited.has('author') &&
                newItem.author !== undefined &&
                (existingItem.author === undefined ||
                  newItem.author.title !== existingItem.author.title ||
                  newItem.author.value !== existingItem.author.value)) {
                existingItem.author = newItem.author;
              }

              if (!this.fieldsBeingEdited.has('agency') &&
                newItem.agency !== undefined &&
                (existingItem.agency === undefined ||
                  newItem.agency.title !== existingItem.agency.title ||
                  newItem.agency.value !== existingItem.agency.value)) {
                existingItem.agency = newItem.agency;
              }

              if (!this.fieldsBeingEdited.has('tag') &&
                newItem.tag !== undefined &&
                (existingItem.tag === undefined ||
                  newItem.tag.title !== existingItem.tag.title ||
                  newItem.tag.value !== existingItem.tag.value)) {
                existingItem.tag = newItem.tag;
              }

              if (!this.fieldsBeingEdited.has('specialOffer') &&
                newItem.specialOffer !== undefined && newItem.specialOffer !== existingItem.specialOffer) {
                existingItem.specialOffer = newItem.specialOffer;
              }

              if (!this.fieldsBeingEdited.has('specialOfferTypes') &&
                newItem.specialOfferTypes !== undefined &&
                (existingItem.specialOfferTypes === undefined ||
                  !areArraysEqual(newItem.specialOfferTypes, existingItem.specialOfferTypes,
                    (a: any, b: any) => a.value === b.value && a.title === b.title))) {
                existingItem.specialOfferTypes = newItem.specialOfferTypes;
              }

              if (!this.fieldsBeingEdited.has('apiFtp') &&
                newItem.apiFtp !== undefined && newItem.apiFtp !== existingItem.apiFtp) {
                existingItem.apiFtp = newItem.apiFtp;
              }

              if (!this.fieldsBeingEdited.has('licence') &&
                newItem.licence !== undefined && newItem.licence !== existingItem.licence) {
                existingItem.licence = newItem.licence;
              }

              if (!this.fieldsBeingEdited.has('dateCreated') &&
                newItem.dateCreated !== undefined && newItem.dateCreated !== existingItem.dateCreated) {
                existingItem.dateCreated = newItem.dateCreated;
              }

              if (newItem.datePublished !== undefined && newItem.datePublished !== existingItem.datePublished) {
                existingItem.datePublished = newItem.datePublished;
              }

              if (!this.fieldsBeingEdited.has('needsTranslationEn') &&
                newItem.needsTranslationEn !== undefined && newItem.needsTranslationEn !== existingItem.needsTranslationEn) {
                existingItem.needsTranslationEn = newItem.needsTranslationEn;
              }

              if (!this.fieldsBeingEdited.has('webPrice') &&
                newItem.webPrice !== undefined && newItem.webPrice !== existingItem.webPrice) {
                existingItem.webPrice = newItem.webPrice;
              }

              if (!this.fieldsBeingEdited.has('printPrice') &&
                newItem.printPrice !== undefined && newItem.printPrice !== existingItem.printPrice) {
                existingItem.printPrice = newItem.printPrice;
              }

              if (!this.fieldsBeingEdited.has('webPriceEur') &&
                newItem.webPriceEur !== undefined && newItem.webPriceEur !== existingItem.webPriceEur) {
                existingItem.webPriceEur = newItem.webPriceEur;
              }

              if (!this.fieldsBeingEdited.has('printPriceEur') &&
                newItem.printPriceEur !== undefined && newItem.printPriceEur !== existingItem.printPriceEur) {
                existingItem.printPriceEur = newItem.printPriceEur;
              }

              if (!this.fieldsBeingEdited.has('videoTitle') &&
                newItem.videoTitle !== undefined && newItem.videoTitle !== existingItem.videoTitle) {
                existingItem.videoTitle = newItem.videoTitle;
              }

              if (newItem.galleryId !== undefined && newItem.galleryId !== existingItem.galleryId) {
                existingItem.galleryId = newItem.galleryId;
              }

              if (newItem.splitFromGallery !== undefined && newItem.splitFromGallery !== existingItem.splitFromGallery) {
                existingItem.splitFromGallery = newItem.splitFromGallery;
              }

              if (newItem.mediaId !== undefined && newItem.mediaId !== existingItem.mediaId) {
                existingItem.mediaId = newItem.mediaId;
              }

              if (newItem.imageCount !== undefined && newItem.imageCount !== existingItem.imageCount) {
                existingItem.imageCount = newItem.imageCount;
              }

              if (newItem.videoDuration !== undefined && newItem.videoDuration !== existingItem.videoDuration) {
                existingItem.videoDuration = newItem.videoDuration;
              }

              if (newItem.isLoaded !== undefined && newItem.isLoaded !== existingItem.isLoaded) {
                existingItem.isLoaded = newItem.isLoaded;
              }

              this.loadDataForSelectedItem();
            }
          });

          function areArraysEqual(arr1: any, arr2: any, compareFunc: any) {
            if (arr1.length !== arr2.length) { return false; }

            for (let i = 0; i < arr1.length; i++) {
              let found = false;
              for (let j = 0; j < arr2.length; j++) {
                if (compareFunc(arr1[i], arr2[j])) {
                  found = true;
                  break;
                }
              }
              if (!found) { return false; }
            }

            return true;
          }

          this.itemsSource.next(this.items);

          if (difference.length) {
            this.appendIdsToSelection(difference.map((i) => i.id));
          }

          logging.info(
            'import.service.ts',
            `Loading finished for tag ${tag}`,
            this.blockingOperations
          );
          this.lastLoadedTime = new Date();
          this.blockingOperations = this.blockingOperations.filter(
            (s) => s.tag != tag
          );
        },
        (error) => {
          logging.info(
            'import.service.ts',
            `Loading failed for tag ${tag}`,
            this.blockingOperations
          );
          this.blockingOperations = this.blockingOperations.filter(
            (s) => s.tag != tag
          );
        }
      );

    this.blockingOperations.push({
      tag,
      subscription,
      type: 'refresh',
    });

    logging.info(
      'import.service.ts',
      `Starting loading with tag ${tag}`,
      this.lastModifiedAt
    );
  }

  //////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
  // DELETING
  //////////////////////////////////////////////////////////////////////////////////////////////////////////////////////

  public deleteSelected() {
    const request: IdsList = {
      ids: this.selectedIds,
    };

    const toDeleteIds = this.selectedIds;

    this.loadingService.setLoading(true);

    const tag: string = this.uuidv4();

    const subscription: Subscription = this.uploadService
      .cmsDeleteMediaItems(request)
      .subscribe(
        (result: any) => {
          this.items = this.items.filter((i) => !toDeleteIds.includes(i.id));
          toDeleteIds.forEach((id: number) => {
            delete this.itemsById[id];
          });

          this.selectedIds = this.selectedIds.filter(
            (id) => !toDeleteIds.includes(id)
          );
          this.selectedIdsSource.next(this.selectedIds);

          this.itemsSource.next(this.items);
          this.snackBarService.showSnackBar(
            'Successfully deleted item(s).',
            'INFO'
          );
          this.loadingService.setLoading(false);
          this.deselectIds(this.selectedIds);

          logging.info('import.service.ts', `Success deleting with tag ${tag}`);
          this.blockingOperations = this.blockingOperations.filter(
            (s) => s.tag != tag
          );
        },
        (error) => {
          this.snackBarService.showSnackBar(
            'Failed deleting item(s).',
            'ERROR'
          );
          this.loadingService.setLoading(false);

          logging.info('import.service.ts', `Failed deleting with tag ${tag}`);
          this.blockingOperations = this.blockingOperations.filter(
            (s) => s.tag != tag
          );
        }
      );

    this.blockingOperations.push({
      tag,
      subscription,
      type: 'delete',
    });

    logging.info('import.service.ts', `Starting deleting with tag ${tag}`);
  }

  private saveSession() {
    const tag: string = this.uuidv4();
    const uploadSession: UploadSession = this.serializeUploadSession();
    const subscription: Subscription = this.uploadService
      .cmsPutUploadSession(uploadSession)
      .subscribe(
        (result: UploadSession) => {
          logging.info('import.service.ts', `Success saving with tag ${tag}`);
          this.blockingOperations = this.blockingOperations.filter(
            (s) => s.tag != tag
          );
          this.isAiStatusDirty = false;
        },
        (error) => {
          logging.info('import.service.ts', `Failed saving with tag ${tag}`);
          this.blockingOperations = this.blockingOperations.filter(
            (s) => s.tag != tag
          );
        }
      );

    this.blockingOperations.push({
      tag,
      subscription,
      type: 'saving',
    });
    logging.info('import.service.ts', `Starting saving with tag ${tag}`);
  }

  public publishSession(
    kind: UploadSessionPublishRequest.KindEnum,
    isNewsletterPush: boolean,
    isNotificationPush: boolean
  ) {
    const tag: string = this.uuidv4();
    this.loadingService.setLoading(true);

    const uploadSession: UploadSession = this.serializeUploadSession();
    const uploadSessionPublishRequest: UploadSessionPublishRequest = {
      kind,
      is_newsletter_push: isNewsletterPush,
      is_notification_push: isNotificationPush,
    };

    this.selectItems([]);
    this.isSaving = true;
    const subscription: Subscription = this.uploadService
      .cmsPutUploadSession(uploadSession)
      .subscribe(
        (result: UploadSession) => {
          this.uploadService
            .cmsPublishUploadSession(uploadSessionPublishRequest, 'response')
            .subscribe(
              (result) => {
                this.loadingService.setLoading(false);
                this.snackBarService.showSnackBar('Saved.', 'INFO');
                this.items = [];
                this.itemsById = {};
                this.selectItems([]);
                this.itemsSource.next(this.items);
                this.selectedIdsSource.next([]);
                this.loadDataForSelectedItem();
                this.refreshErrorData();
                this.unselectAll();
                this.isSaving = false;

                logging.info(
                  'import.service.ts',
                  `Success publishing with tag ${tag}`
                );
                this.lastLoadedTime = new Date();
                this.blockingOperations = this.blockingOperations.filter(
                  (s) => s.tag != tag
                );
              },
              (error) => {
                const errorResponse: ErrorResponse = error.error;

                this.currentErrorResponse = errorResponse;
                this.errorResponseSource.next(errorResponse);
                this.errorIdsSource.next(
                  errorResponse.errors.map((e) => parseInt(e.item_id ?? '-1'))
                );
                this.refreshErrorData();
                this.loadingService.setLoading(false);
                this.snackBarService.showSnackBar('Error saving.', 'ERROR');
                this.isSaving = false;

                logging.info(
                  'import.service.ts',
                  `Failed publishing with tag ${tag}`
                );
                this.blockingOperations = this.blockingOperations.filter(
                  (s) => s.tag != tag
                );
              }
            );
        },
        (error) => {
          logging.info(
            'import.service.ts',
            `Failed publishing with tag ${tag}`
          );
          this.blockingOperations = this.blockingOperations.filter(
            (s) => s.tag != tag
          );
        }
      );

    this.blockingOperations.push({
      tag,
      subscription,
      type: 'publish',
    });
    logging.info('import.service.ts', `Starting publishing with tag ${tag}`);
  }

  //////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
  // SELECTION
  //////////////////////////////////////////////////////////////////////////////////////////////////////////////////////

  appendIdsToSelection(ids: number[]) {
    this.selectItems(this.selectedIds.concat(ids));
  }

  selectItems(itemIds: number[]) {
    this.selectedIds = itemIds;
    this.selectedIdsSource.next(itemIds);
    this.selectItemsSource.next(itemIds);
    this.loadDataForSelectedItem();
    this.refreshErrorData();
  }

  deselectIds(ids: number[]) {
    this.selectItems(this.selectedIds.filter((i) => !ids.includes(i)));
  }

  selectAll() {
    this.selectAllSource.next(true);
  }

  unselectAll() {
    this.selectAllSource.next(false);
  }

  //////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
  //
  //////////////////////////////////////////////////////////////////////////////////////////////////////////////////////

  refreshErrorData() {
    this.refreshEditingErrorsSource.next();
  }

  loadDataForSelectedItem() {
    // Pomažemo funkcija koja će provjeriti je li polje u fokusu prije ažuriranja
    const shouldUpdateField = (fieldName: string) => !this.fieldsBeingEdited.has(fieldName);

    // Ažuriranje headline polja
    if (shouldUpdateField('headline')) {
      this.editingHeadlineSource.next(
        ComputeFields.computeAutocompleteFieldValue(
          this.selectedIds,
          this.itemsById,
          (i: ImportItem) => i.headline
        )
      );
    }

    // Ažuriranje caption polja
    if (shouldUpdateField('caption')) {
      this.editingCaptionSource.next(
        ComputeFields.computeFieldValue(
          this.selectedIds,
          this.itemsById,
          (i: ImportItem) => i.caption
        )
      );
    }

    // Ažuriranje people polja
    if (shouldUpdateField('people')) {
      this.editingPeopleSource.next(
        ComputeFields.computeMultipleAutocompleteFieldValue(
          this.selectedIds,
          this.itemsById,
          (i: ImportItem) => i.people
        )
      );
    }

    // Ažuriranje keywords polja
    if (shouldUpdateField('keywords')) {
      this.editingKeywordsSource.next(
        ComputeFields.computeMultipleAutocompleteFieldValue(
          this.selectedIds,
          this.itemsById,
          (i: ImportItem) => i.keywords
        )
      );
    }

    // Ažuriranje categories polja
    if (shouldUpdateField('categories')) {
      this.editingCategoriesSource.next(
        ComputeFields.computeAutocompleteFieldValue(
          this.selectedIds,
          this.itemsById,
          (i: ImportItem) => i.categories
        )
      );
    }

    // Ažuriranje videoTitle polja
    if (shouldUpdateField('videoTitle')) {
      this.videoTitleSource.next(
        ComputeFields.computeFieldValue(
          this.selectedIds,
          this.itemsById,
          (i: ImportItem) => i.videoTitle
        )
      );
    }

    // Ažuriranje author polja
    if (shouldUpdateField('author')) {
      this.editingAuthorSource.next(
        ComputeFields.computeAutocompleteFieldValue(
          this.selectedIds,
          this.itemsById,
          (i: ImportItem) => i.author
        )
      );
    }

    // Ažuriranje agency polja
    if (shouldUpdateField('agency')) {
      this.editingAgencySource.next(
        ComputeFields.computeAutocompleteFieldValue(
          this.selectedIds,
          this.itemsById,
          (i: ImportItem) => i.agency
        )
      );
    }

    // Ažuriranje tag polja
    if (shouldUpdateField('tag')) {
      this.editingTagSource.next(
        ComputeFields.computeAutocompleteFieldValue(
          this.selectedIds,
          this.itemsById,
          (i: ImportItem) => i.tag
        )
      );
    }

    // Ažuriranje specialOffer polja
    if (shouldUpdateField('specialOffer')) {
      this.editingSpecialOfferSource.next(
        ComputeFields.computeBooleanFieldValue(
          this.selectedIds,
          this.itemsById,
          (i: ImportItem) => i.specialOffer
        )
      );
    }

    // Ažuriranje specialOfferTypes polja
    if (shouldUpdateField('specialOfferTypes')) {
      this.editingSpecialOfferTypesSource.next(
        ComputeFields.computeMultipleAutocompleteFieldValue(
          this.selectedIds,
          this.itemsById,
          (i: ImportItem) => i.specialOfferTypes
        )
      );
    }

    // Ažuriranje apiFtp polja
    if (shouldUpdateField('apiFtp')) {
      this.editingApiFtpSource.next(
        ComputeFields.computeBooleanFieldValue(
          this.selectedIds,
          this.itemsById,
          (i: ImportItem) => i.apiFtp
        )
      );
    }

    // Ažuriranje licence polja
    if (shouldUpdateField('licence')) {
      this.editingLicenceSource.next(
        ComputeFields.computeFieldValue(
          this.selectedIds,
          this.itemsById,
          (i: ImportItem) => i.licence
        )
      );
    }

    // Ažuriranje dateCreated polja
    if (shouldUpdateField('dateCreated')) {
      this.editingDateCreatedSource.next(
        ComputeFields.computeFieldValue(
          this.selectedIds,
          this.itemsById,
          (i: ImportItem) => i.dateCreated
        )
      );
    }

    // Ažuriranje needsTranslationEn polja
    if (shouldUpdateField('needsTranslationEn')) {
      this.editingNeedsTranslationEnSource.next(
        ComputeFields.computeBooleanFieldValue(
          this.selectedIds,
          this.itemsById,
          (i: ImportItem) => i.needsTranslationEn
        )
      );
    }

    // Ažuriranje captionEn polja
    if (shouldUpdateField('captionEn')) {
      this.editingCaptionEnSource.next(
        ComputeFields.computeFieldValue(
          this.selectedIds,
          this.itemsById,
          (i: ImportItem) => i.captionEn
        )
      );
    }

    // Ažuriranje keywordsEn polja
    if (shouldUpdateField('keywordsEn')) {
      this.editingKeywordsEnSource.next(
        ComputeFields.computeMultipleAutocompleteFieldValue(
          this.selectedIds,
          this.itemsById,
          (i: ImportItem) => i.keywordsEn
        )
      );
    }

    // Ažuriranje printPrice polja
    if (shouldUpdateField('printPrice')) {
      this.editingPrintPriceSource.next(
        ComputeFields.computeNumberFieldValue(
          this.selectedIds,
          this.itemsById,
          (i: ImportItem) => i.printPrice
        )
      );
    }

    // Ažuriranje webPrice polja
    if (shouldUpdateField('webPrice')) {
      this.editingWebPriceSource.next(
        ComputeFields.computeNumberFieldValue(
          this.selectedIds,
          this.itemsById,
          (i: ImportItem) => i.webPrice
        )
      );
    }

    // Ažuriranje printPriceEur polja
    if (shouldUpdateField('printPriceEur')) {
      this.editingPrintPriceEurSource.next(
        ComputeFields.computeNumberFieldValue(
          this.selectedIds,
          this.itemsById,
          (i: ImportItem) => i.printPriceEur
        )
      );
    }

    // Ažuriranje webPriceEur polja
    if (shouldUpdateField('webPriceEur')) {
      this.editingWebPriceEurSource.next(
        ComputeFields.computeNumberFieldValue(
          this.selectedIds,
          this.itemsById,
          (i: ImportItem) => i.webPriceEur
        )
      );
    }

    // For faces, gather all faces from all selected items
    const allFaces: FaceDetection[] = [];

    this.selectedIds.forEach(id => {
      const item = this.itemsById[id];
      if (item && item.faces && item.faces.length > 0) {
        item.faces.forEach(face => {
          // Make sure thumbnails are set but avoid creating new objects
          if (!face.thumbnail) {
            face.thumbnail = item.previewUrl || '/assets/no-image.svg';
          }
          if (!face.mediaPreview) {
            face.mediaPreview = item.previewUrl;
          }

          allFaces.push(face);
        });
      }
    });

    // Always emit the faces array (even if empty) so subscribers get updates
    this.editingFacesSource.next(allFaces);

    this.calculateSuffix();

    logging.info('import.service.ts', 'loadDataForSelectedItem');
  }

  /////////////////////////////
  // FIELDS
  /////////////////////////////

  getSuffixForCaption() {
    const author = ComputeFields.computeAutocompleteFieldValue(
      this.selectedIds,
      this.itemsById,
      (i: ImportItem) => i.author
    );
    const agency = ComputeFields.computeAutocompleteFieldValue(
      this.selectedIds,
      this.itemsById,
      (i: ImportItem) => i.agency
    );
    const people = ComputeFields.computeMultipleAutocompleteFieldValue(
      this.selectedIds,
      this.itemsById,
      (i: ImportItem) => i.people
    );

    if (
      author.value == -3 ||
      agency.value == -3 ||
      people.find((e) => e.value == -3) != undefined
    ) {
      return '<multiple values>';
    }

    if (
      author != undefined &&
      agency != undefined &&
      author?.value != -1 &&
      agency?.value != -1
    ) {
      const _author = author!.title;
      const _agency = agency!.title;
      let _people = people?.map((p) => p.title).join(', ');
      if (_people != undefined) {
        _people = _people + '\n';
      } else {
        _people = '';
      }
      return `${_people}Photo: ${_author}/${_agency}`;
    }

    return 'This will be calculated automatically.';
  }

  calculateSuffix() {
    const suffix = this.getSuffixForCaption();
    this.editingSuffixSource.next(suffix);
  }

  dismissErrorsAfterUserInput(field: string, value: string) {
    // TODO: API returns wrong field name.
    if (field === 'categories') {
      field = 'category';
    }

    if (this.currentErrorResponse === undefined) {
      return;
    }

    const errorToBeRemoved: ModelError[] = [];

    for (const error of this.currentErrorResponse!.errors) {
      // Clear error on editing.
      if (
        this.selectedIds.includes(parseInt(error.item_id!)) &&
        error.field_name === field
      ) {
        errorToBeRemoved.push(error);
      }
    }

    this.currentErrorResponse!.errors =
      this.currentErrorResponse!.errors.filter(
        (e) => !errorToBeRemoved.includes(e)
      );
    this.errorResponseSource.next(this.currentErrorResponse!);
    this.errorIdsSource.next(
      this.currentErrorResponse!.errors.map((e) => parseInt(e.item_id ?? '-1'))
    );
    this.refreshErrorData();
  }

  setField(field: string, value: any) {
    this.dismissErrorsAfterUserInput(field, value);

    let setter: (item: ImportItem) => void;

    if (this.selectedIds.length) {
      switch (field) {
        case 'videoTitle':
          setter = (item: ImportItem) => (item.videoTitle = value);
          break;

        case 'headline':
          setter = (item: ImportItem) => (item.headline = value);
          break;

        case 'caption':
          setter = (item: ImportItem) => (item.caption = value);
          break;

        case 'people':
          setter = (item: ImportItem) => (item.people = value);
          break;

        case 'keywords':
          setter = (item: ImportItem) => (item.keywords = value);
          break;

        case 'categories':
          setter = (item: ImportItem) => (item.categories = value);
          break;

        case 'author':
          setter = (item: ImportItem) => (item.author = value);
          break;

        case 'agency':
          setter = (item: ImportItem) => (item.agency = value);
          break;

        case 'tag':
          setter = (item: ImportItem) => (item.tag = value);
          break;

        case 'specialOffer':
          setter = (item: ImportItem) => (item.specialOffer = value);
          break;

        case 'specialOfferTypes':
          setter = (item: ImportItem) => (item.specialOfferTypes = value);
          break;

        case 'apiFtp':
          setter = (item: ImportItem) => (item.apiFtp = value);
          break;

        case 'licence':
          setter = (item: ImportItem) => (item.licence = value);
          break;

        case 'dateCreated':
          setter = (item: ImportItem) => (item.dateCreated = value);
          break;

        case 'needsTranslationEn':
          setter = (item: ImportItem) => (item.needsTranslationEn = value);
          break;

        case 'captionEn':
          setter = (item: ImportItem) => (item.captionEn = value);
          break;

        case 'keywordsEn':
          setter = (item: ImportItem) => (item.keywordsEn = value);
          break;

        case 'webPrice':
          setter = (item: ImportItem) => (item.webPrice = value);
          break;

        case 'printPrice':
          setter = (item: ImportItem) => (item.printPrice = value);
          break;

        case 'webPriceEur':
          setter = (item: ImportItem) => (item.webPriceEur = value);
          break;

        case 'printPriceEur':
          setter = (item: ImportItem) => (item.printPriceEur = value);
          break;
      }
    }

    const selectedItems = this.selectedIds
      .map((id) => this.itemsById[id])
      .filter((i) => i != undefined);
    selectedItems.forEach((item) => setter(item));

    this.calculateSuffix();

    this.queueSaveSession();
  }

  /////////////////////////////
  // Saving
  /////////////////////////////

  getHeadline(mediaItem: MediaItem): string {
    if (mediaItem.headline == undefined) { return ''; }

    if (mediaItem.headline!.length == 0) { return ''; }

    return mediaItem.headline![0].content ?? '';
  }

  checkedIsLocked(mediaItem: MediaItem) {
    if (mediaItem.editing_lock != undefined) {
      const raw: string =
        ((mediaItem.editing_lock.locked_at! as unknown) as string) + 'Z';
      const lockedAt = new Date(raw);
      const newDate = new Date();

      if (lockedAt != undefined) {
        const timeDiff = newDate.getTime() - lockedAt!.getTime();

        // More than 5 minutes.
        return timeDiff < 15 * 1000;
      }
      return;
    }

    return false;
  }

  lockedByName(mediaItem: MediaItem): string | undefined {
    if (this.checkedIsLocked(mediaItem)) {
      return (
        mediaItem.editing_lock!.locked_by!.first_name +
        ' ' +
        mediaItem.editing_lock!.locked_by!.last_name
      );
    }

    return undefined;
  }

  deserializeUploadSession(uploadSession: UploadSession): ImportItem[] {
    return uploadSession.media_list.map((uploadSessionItem) => {
      const width = 1;
      const height = 1;

      let agency: AutocompleteOption | undefined;
      if (uploadSessionItem.agency != undefined) {
        agency = {
          title: uploadSessionItem.agency!.name!,
          value: uploadSessionItem.agency!.id!,
          isSelectable: true,
        };
      }

      let author: AutocompleteOption | undefined;
      if (uploadSessionItem.author != undefined) {
        author = {
          title: uploadSessionItem.author!.name,
          value: uploadSessionItem.author!.id,
          isSelectable: true,
        };
      }

      let categories: AutocompleteOption | undefined;

      if (uploadSessionItem.category != undefined) {
        categories = {
          title:
            uploadSessionItem.category!.name.find(
              (t: Translatable) => t.language == 'HR'
            )?.content ?? '',
          value: uploadSessionItem.category!.id!,
          isSelectable: true,
        };
      }

      if (uploadSessionItem.agency != undefined) {
        agency = {
          title: uploadSessionItem.agency!.name!,
          value: uploadSessionItem.agency!.id!,
          isSelectable: true,
        };
      }

      let specialOfferTypes: AutocompleteOption[] | undefined;
      if (uploadSessionItem.special_offer_types != undefined) {
        specialOfferTypes = uploadSessionItem.special_offer_types!.map((k) => {
          return {
            title:
              k.name?.find((t: Translatable) => t.language == 'HR')?.content ??
              '',
            value: k.id,
            isSelectable: true,
          };
        });
      }

      let tag: AutocompleteOption | undefined;

      if (
        uploadSessionItem.tag_list != undefined &&
        uploadSessionItem.tag_list.length != 0
      ) {
        tag = {
          title:
            uploadSessionItem.tag_list[0].name.find(
              (t: Translatable) => t.language == 'HR'
            )?.content ?? '',
          value: uploadSessionItem.tag_list[0].id,
          isSelectable: true,
        };
      }

      const {aiStatus, isAiProcessing } = AIStatusTrackingService.parseStatus({
        ai_status: uploadSessionItem.ai_status,
        face_recognition_status: uploadSessionItem.face_recognition_status
      });

      let itemFaces: FaceDetection[] = [];
      if (uploadSessionItem.face_detections && uploadSessionItem.face_detections.length > 0) {
        itemFaces = uploadSessionItem.face_detections.map((fd: any) => {
          // If thumbnail is null or missing, use mediaPreview which should always be available
          const thumbnail = fd.thumbnail || uploadSessionItem.preview?.preview_url || '/assets/no-image.svg';

          return {
            id: fd.id,
            thumbnail,
            mediaId: fd.mediaId || parseInt(uploadSessionItem.id),
            mediaPreview: fd.mediaPreview || uploadSessionItem.preview?.preview_url,
            person: fd.person ? {
              title: fd.person.title,
              value: fd.person.value,
              isSelectable: true
            } : undefined,
            x: fd.x,
            y: fd.y,
            width: fd.width,
            height: fd.height,
            x_percent: fd.x_percent,
            y_percent: fd.y_percent,
            width_percent: fd.width_percent,
            height_percent: fd.height_percent,
          };
        });
      }

      const object: ImportItem = {
        id: parseInt(uploadSessionItem.id),
        width,
        height,
        aspectRatio: uploadSessionItem.aspect_ratio,
        previewUrl: uploadSessionItem.preview?.preview_url,
        blurPreviewUrl: uploadSessionItem.preview?.preview_url_blur,
        largePreviewUrl: uploadSessionItem.preview?.preview_url_large,
        previewVideoLoop: uploadSessionItem.preview?.preview_video_loop,
        previewVideo720d: uploadSessionItem.preview?.preview_video_720p,

        isVideo: uploadSessionItem.preview?.preview_video_720p != null,
        isLocked: this.checkedIsLocked(uploadSessionItem),
        lockedBy: this.lockedByName(uploadSessionItem),
        faces: itemFaces,

        title:
          uploadSessionItem.headline?.find(
            (t: Translatable) => t.language == 'HR'
          )?.content ?? '',
        headline: {
          title: this.getHeadline(uploadSessionItem),
          value: -1,
          isSelectable: true,
        },

        caption:
          uploadSessionItem.caption?.find(
            (t: Translatable) => t.language == 'HR'
          )?.content ?? '',
        people: uploadSessionItem.people_list!.map((p) => {
          return {
            title: p.name,
            value: p.id,
            isSelectable: true,
          };
        }),
        keywords: uploadSessionItem.keywords_list!.map((k) => {
          return {
            title:
              k.name?.find((t: Translatable) => t.language == 'HR')?.content ??
              '',
            value: k.id,
            isSelectable: true,
          };
        }),

        categories,
        author,
        agency,
        tag,
        specialOffer: uploadSessionItem.is_special_offer,
        specialOfferTypes,
        apiFtp: uploadSessionItem.api_ftp,

        printPrice: uploadSessionItem.print_price,
        webPrice: uploadSessionItem.web_price,

        videoTitle:
          uploadSessionItem.video_title?.find(
            (t: Translatable) => t.language == 'HR'
          )?.content ?? '',

        dateCreated: uploadSessionItem.date_created,
        needsTranslationEn: uploadSessionItem.needs_translation,
        captionEn:
          uploadSessionItem.caption?.find(
            (t: Translatable) => t.language == 'EN'
          )?.content ?? '',
        keywordsEn: uploadSessionItem.keywords_list!.map((k) => {
          return {
            title:
              k.name?.find((t: Translatable) => t.language == 'EN')?.content ??
              '',
            value: k.id,
            isSelectable: true,
          };
        }),

        printPriceEur: uploadSessionItem.print_price_eur,
        webPriceEur: uploadSessionItem.web_price_eur,

        isAiProcessing,
        aiStatus,
      };

      return object;
    });
  }

  serializeUploadSession(): UploadSession {
    const mediaList: MediaItem[] = this.items.map((item) => {
      // Here we have to fill out such information and take in account
      // where we have a new upload item or if the upload item is something
      // from an old session.

      let agency: Agency | undefined;
      if (item.agency != undefined) {
        agency = {
          id: item.agency.value,
          name: item.agency.title,
        };
      }

      let author: Author | undefined;
      if (item.author != undefined) {
        author = {
          id: item.author.value,
          name: item.author.title,
        };
      }

      let category: Category | undefined;
      if (item.categories != undefined) {
        category = {
          id: item.categories.value,
          name: [],
        };
      }

      const keywordsList: Keyword[] = [];
      if (item.keywords != undefined) {
        item.keywords.forEach((k) => {
          keywordsList.push({
            id: k.value,
            name: [{ language: 'HR', content: k.title }],
          });
        });
      }

      if (item.keywordsEn != undefined) {
        item.keywordsEn.forEach((k) => {
          keywordsList.push({
            id: k.value,
            name: [{ language: 'EN', content: k.title }],
          });
        });
      }

      let licenceType: LicenceType | undefined;
      if (item.author != undefined) {
        author = {
          id: item.author.value,
          name: item.author.title,
        };
      }

      const peopleList: Array<Person> = [];
      if (item.people != undefined) {
        item.people.forEach((k) => {
          peopleList.push({
            id: k.value,
            name: k.title,
          });
        });
      }

      let specialOfferTypes: SpecialOfferType[] | undefined;
      if (item.specialOfferTypes != undefined) {
        specialOfferTypes = item.specialOfferTypes.map((so) => {
          return {
            id: so.value,
            name: [],
          };
        });
      }

      let tagList: Array<Tag> = [];
      if (item.tag != undefined) {
        tagList = [
          {
            id: item.tag!.value,
            name: [],
          },
        ];
      }

      const uploadItem: MediaItem = {
        // This is required, but not for sending to the server.
        editing_lock: undefined,
        has_preview: false,
        kind: 'PHOTO',

        id: item.id.toString(),
        agency,
        api_ftp: item.apiFtp,
        aspect_ratio: undefined,
        author,

        caption: [
          { language: 'HR', content: item.caption },
          { language: 'EN', content: item.captionEn },
        ],
        category,
        date_created: undefined, // Backend team needs to calc this
        gallery_id: item.headline?.value,
        headline: [{ language: 'HR', content: item.headline?.title }],
        video_title: [{ language: 'HR', content: item.videoTitle }],
        is_special_offer: item.specialOffer ?? false,
        keywords_list: keywordsList,
        needs_translation: item.needsTranslationEn,
        people_list: peopleList,
        // Send face data in the expected face_detections field as well
        face_detections: item.faces ? item.faces.map(face => ({
          id: face.id,
          person: face.person ? {
            id: face.person.value,
            name: face.person.title,
            value: face.person.value,
            title: face.person.title,
            isSelectable: true
          } : null,
          // Include other essential face properties
          x: face.x || 0,
          y: face.y || 0,
          width: face.width || 100,
          height: face.height || 100,
          mediaId: face.mediaId || item.id,
          confidence: face.confidence || 1.0
        })) : [],
        preview: {
          preview_url: item.previewUrl,
          preview_url_blur: item.blurPreviewUrl,
          preview_url_large: item.largePreviewUrl,
          preview_video_720p: item.previewVideo720d,
          preview_video_loop: item.previewVideoLoop,
        },
        special_offer_types: specialOfferTypes,
        tag_list: tagList,
        print_price: item.printPrice,
        web_price: item.webPrice,
        web_price_eur: item.webPriceEur,
        print_price_eur: item.printPriceEur,
      };

      return uploadItem;
    });

    return {
      media_list: mediaList,
      ai_content: this.aiContentSource.value,
      ai_face: this.aiFaceSource.value,
      ai_crop: this.aiCropSource.value
    };
  }

  public queueSaveSession() {
    this.saveQueueSource.next(void 0);
  }

  private setUpSaveSession() {
    this.saveQueueData
      .pipe(
        skipWhile((x) => this.isSaving),
        bufferTime(1000)
      )
      .subscribe((arr) => {
        if (arr.length == 0) { return; }

        this.saveSession();
      });
  }

  //////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
  // UPLOADING FILES
  //////////////////////////////////////////////////////////////////////////////////////////////////////////////////////

  private _uploadInProgress$: BehaviorSubject<boolean> =
    new BehaviorSubject<boolean>(false);
  public uploadInProgress$: Observable<boolean> =
    this._uploadInProgress$.asObservable();

  public uploadFile(files: any) {
    this._uploadInProgress$.next(true);
    for (const item of files) {
      item.progress = 0;
      this.files.push(item);

      this.uploadService
        .cmsUploadUploadSessionFile(item, 'events', true)
        .subscribe((result: any) => {
          if (result.type == 1) {
            item.progress = (result.loaded / result.total) * 100.0;

            // It's finished.
            if (item.progress >= 100.0) {
              setTimeout(() => {
                this.files = this.files.filter((file) => file !== item);
                this._uploadInProgress$.next(!!this.files.length); // TODO: "this.files" is the wrong target condition
                this.uploadingFilesSource.next(this.files);
                this.loadSession();
              }, 1000);
            }
          }
        });
    }

    this.uploadingFilesSource.next(this.files);
  }

  public replaceFile(files: any, itemId: number) {
    this._uploadInProgress$.next(true);
    for (const item of files) {
      item.progress = 0;
      this.files.push(item);
      // TODO: Implement
      this.uploadService
        .cmsReplaceMediaMedia(itemId, item, 'events', true)
        .subscribe((result: any) => {
          if (result.type == 1) {
            item.progress = (result.loaded / result.total) * 100.0;

            // It's finished.
            if (item.progress >= 100.0) {
              setTimeout(() => {
                this.files = this.files.filter((file) => file !== item);
                this._uploadInProgress$.next(!!this.files.length); // TODO: "this.files" is the wrong target condition
                this.uploadingFilesSource.next(this.files);
                window.location.reload();
              }, 1000);
            }
          }
        });
    }

    this.uploadingFilesSource.next(this.files);
  }

  //////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
  // REORDERING
  //////////////////////////////////////////////////////////////////////////////////////////////////////////////////////

  private move(array: any[], oldIndex: number, newIndex: number) {
    const temp = array[newIndex];
    array[newIndex] = array[oldIndex];
    array[oldIndex] = temp;
  }

  public moveItemFromIndexToIndex(oldIndex: number, newIndex: number) {
    this.move(this.items, oldIndex, newIndex);
  }

  //////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
  // ACTIONS
  //////////////////////////////////////////////////////////////////////////////////////////////////////////////////////

  escapeRegExp(string: string) {
    return string.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'); // $& means the whole matched string
  }

  replaceInString(string: string, needle: string, withValue: string): string {
    const escapedRegexNeedle = this.escapeRegExp(needle);
    return string.replace(new RegExp(escapedRegexNeedle, 'gi'), withValue);
  }

  replace(
    field: string,
    what: AutocompleteOption,
    withWhat: AutocompleteOption
  ) {
    this.items.forEach((item) => {
      if (!this.selectedIds.includes(item.id)) { return; }

      let dirtyItem = false;

      switch (field) {
        case 'people':
          if (item.people?.find((p) => p.value == what.value)) {
            item.people = item.people.map((ao) => {
              if (ao.value == what.value) {
                dirtyItem = true;
                return withWhat;
              }

              return ao;
            });
          }

          break;

        case 'keywords':
          if (item.keywords?.find((p) => p.title == what.title)) {
            item.keywords = item.keywords.map((ao) => {
              if (ao.title == what.title) {
                dirtyItem = true;
                return withWhat;
              }

              return ao;
            });
          }

          if (item.keywordsEn?.find((p) => p.value == what.value)) {
            item.keywordsEn = item.keywordsEn.map((ao) => {
              if (ao.value == what.value) {
                dirtyItem = true;
                return withWhat;
              }

              return ao;
            });
          }

          break;

        case 'headline':
          if (item?.headline?.title.includes(what.title)) {
            dirtyItem = true;
            if (item.headline != undefined) {
              item.headline = {
                title: this.replaceInString(
                  item.headline!.title,
                  what.title,
                  withWhat.title
                ),
                isSelectable: item.headline!.isSelectable,
                value: item.headline!.value,
              };
              item.title = item.headline!.title;
            }
          }

          break;

        case 'categories':
          if (item.categories?.value == what.value) {
            dirtyItem = true;
            item.categories = withWhat;
          }

          break;

        case 'author':
          if (item.author?.value == what.value) {
            dirtyItem = true;
            item.author = withWhat;
          }

          break;

        case 'agency':
          if (item.agency?.value == what.value) {
            dirtyItem = true;
            item.agency = withWhat;
          }

          break;

        case 'tag':
          if (item.tag?.value == what.value) {
            dirtyItem = true;
            item.tag = withWhat;
          }

          break;

        case 'caption':
          if (item.caption?.includes(what.title)) {
            dirtyItem = true;
            item.caption = this.replaceInString(
              item.caption!,
              what.title,
              withWhat.title
            );
          }

          break;
      }
    });

    this.loadDataForSelectedItem();
    this.queueSaveSession();
  }

  append(field: string, what: AutocompleteOption) {
    this.items.forEach((item) => {
      if (!this.selectedIds.includes(item.id)) { return; }

      switch (field) {
        case 'people':
          this.itemsById[item.id].people = this.itemsById[
            item.id
          ].people?.concat([what]);
          break;

        case 'keywords':
          this.itemsById[item.id].keywords = this.itemsById[
            item.id
          ].keywords?.concat([what]);
          break;

        case 'keywordsEn':
          this.itemsById[item.id].keywordsEn = this.itemsById[
            item.id
          ].keywordsEn?.concat([what]);
          break;

        case 'caption':
          this.itemsById[item.id].caption += what.title;
          break;
      }
    });

    this.loadDataForSelectedItem();
    this.queueSaveSession();
  }

  /**
   * Updates a face-person association across all selected items
   * @param faceId The ID of the face to update
   * @param person The person to associate with the face (or undefined to clear)
   */
  public updateFacePerson(faceId: number, person: AutocompleteOption | undefined): void {
    // Find all items with this face and update them
    this.selectedIds.forEach(id => {
      const item = this.itemsById[id];
      if (item && item.faces) {
        const faceIndex = item.faces.findIndex(f => f.id === faceId);
        if (faceIndex >= 0) {
          // Update the person for this face
          item.faces[faceIndex].person = person;
        }
      }
    });

    // Refresh data for the UI
    this.loadDataForSelectedItem();

    // Queue a save to send to the backend
    this.queueSaveSession();
  }

  stopStatusTracking() {
    this.aiStatusTrackingService.stopTracking();
  }
}
